import React, { useState, useEffect, useRef } from 'react';
import { PhoneCall, Save, ArrowLeft, Plus, Trash2, MoveRight, Hash, Mic2, PlayCircle, PhoneOff, Mail, Zap, Code, GitBranch, Volume2, Upload, Play, Rocket, CheckCircle, AlertCircle, Loader2, Users } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import GraphFlowDiagram from 'components/IVR/GraphFlowDiagram';
import VariableMenu from 'components/IVR/VariableMenu';
import OrganizationFilter from '../../components/Filters/OrganizationFilter';
import { useAuth } from '../../hooks/useAuth';
import { ivrFlowApi, audioApi } from '../../services/ivrApi';
import { stateDetailsService } from '../../api/services/stateDetails.service';
import { ivrFlowsService } from '../../api/services/ivrFlows.service';
import { buildNodesFromStates, nodesFromImportedFlow, previewSwitchPush, applySwitchPush } from '../../api/services/ivrDeploy.service';
import type { SwitchPushPlan, SwitchPushOptions } from '../../api/services/ivrDeploy.service';
import SwitchPushPreview from 'components/IVR/SwitchPushPreview';
import { phonesService } from '../../api/services/phones.service';
import { transferProfilesService, TransferProfile } from '../../api/services/transferProfiles.service';
import { aiAgentsService, AiAgent } from '../../api/services/aiAgents.service';
import type { PhoneNumber } from '../../types';

interface APICallConfig {
  url: string;
  method: 'GET' | 'POST' | 'PUT';
  headers?: { key: string; value: string }[];
  body?: string;
  responseMapping?: { key: string; variable: string }[];
  onSuccess?: string; // next node id
  onError?: string; // next node id
}

interface TwiMLConfig {
  xml: string;
  validateXML: boolean;
}

interface AudioConfig {
  text?: string;
  useTextToSpeech?: boolean;
  ttsVoice?: string;
  ttsLanguage?: string;
  audioFiles?: string[];
  useAudioFirst?: boolean;
}

interface IVRNode {
  id: string;
  // ⚠ A LOCAL redeclaration of the node type. types/ivr.ts owns IVRNodeType,
  // and this list has to be kept in step by hand — adding 'queue' meant
  // editing it in three places before the build went green. Worth collapsing
  // onto the shared type; until then, edit all three.
  type: 'greeting' | 'menu' | 'input' | 'transfer' | 'voicemail' | 'hangup' | 'ai_agent' | 'api_call' | 'queue' | 'twiml';
  label: string;
  message: string;
  audioConfig?: AudioConfig;
  // value = per-choice payload: a destination, product id, or agent wss:// URL
  // (how a menu routes to a third-party agent). -> type_meta.choices[].value
  options?: { digit: string; label: string; value?: string; nextNode: string }[];
  nextNode?: string;   // single next-state transition (non-menu nodes) -> type_meta.input.nextState
  // Transfer: number (PSTN) | sip (dial/refer) | agent (voice agent) | other
  transferType?: 'number' | 'sip' | 'agent' | 'other';
  transferNumber?: string;     // PSTN destination
  sipEndpoint?: string;        // sip: URI / endpoint
  transferMethod?: 'refer' | 'dial';
  transferProfileId?: number;  // selected sip_transfer_profile
  transferProfileName?: string;
  // AI / voice agent: internal (managed by us) vs external (third-party wss URL)
  aiAgent?: string;            // internal managed agent id/name
  agentMode?: 'internal' | 'external';
  agentUrl?: string;           // wss:// agent endpoint (inbound)  -> input.value
  agentUrlOut?: string;        // wss:// agent endpoint (outbound) -> input.valueOut
  timeout?: number;
  apiCall?: APICallConfig;
  twiml?: TwiMLConfig;
}

interface IVRConfig {
  name: string;
  description: string;
  provider: 'twilio' | 'exotel' | 'both';
  phoneNumber: string;
  language: string;
  voice: string;
  recordCalls: boolean;
  transcribeCalls: boolean;
  businessHours: string;
  afterHoursBehavior: string;
  nodes: IVRNode[];
  currentNodeId: string;
  business_id?: string;
  organization_id?: string;
  operator_id?: string;
  service_id?: string;
}

const IVRCreation: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentOrganization } = useAuth();

  // Check if we're in edit mode
  const flowId = searchParams.get('id');
  const isEditMode = !!flowId;

  const [loading, setLoading] = useState(false);
  const [loadingFlow, setLoadingFlow] = useState(false);
  // Pushing to the SWITCH writes the rows the AGI actually reads when a call arrives.
  // (The old "Deploy" button called CRUD endpoints that do not exist and was removed.)
  const [switchPlan, setSwitchPlan] = useState<SwitchPushPlan | null>(null);
  const [switchBusy, setSwitchBusy] = useState(false);
  const [switchError, setSwitchError] = useState<string | null>(null);
  const [switchDone, setSwitchDone] = useState<SwitchPushPlan | null>(null);
  // Default = keep the switch's recording folder and let the backend pick the
  // switch the flow's numbers live on. Never a hard-coded provider.
  const [switchOptions, setSwitchOptions] = useState<SwitchPushOptions>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'design' | 'flowchart' | 'settings'>('design');
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([]);
  const [loadingPhones, setLoadingPhones] = useState(false);
  const [ttsPreviewing, setTtsPreviewing] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [audioAssets, setAudioAssets] = useState<Array<{ id: number; name?: string; url?: string; language?: string }>>([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  // Configured SIP transfer profiles, offered when a Transfer node uses SIP.
  const [transferProfiles, setTransferProfiles] = useState<TransferProfile[]>([]);
  useEffect(() => {
    (async () => {
      const res = await transferProfilesService.list(currentOrganization?.id);
      if (res.success) setTransferProfiles(res.data || []);
    })();
  }, [currentOrganization?.id]);

  // Managed AI agents — their wsurl is what an agent node's agentUrl points at.
  const [aiAgents, setAiAgents] = useState<AiAgent[]>([]);
  useEffect(() => {
    (async () => {
      const res = await aiAgentsService.list(currentOrganization?.id);
      if (res.success) setAiAgents(res.data || []);
    })();
  }, [currentOrganization?.id]);

  const [config, setConfig] = useState<IVRConfig>({
    name: '',
    description: '',
    provider: 'twilio',
    phoneNumber: '',
    language: 'en-US',
    voice: 'alice',
    recordCalls: true,
    transcribeCalls: false,
    businessHours: 'default',
    afterHoursBehavior: 'voicemail',
    nodes: [
      {
        id: 'start',
        type: 'greeting',
        label: 'Welcome Greeting',
        message: 'Thank you for calling. Please listen carefully to the following options.',
        timeout: 5,
      },
    ],
    currentNodeId: 'start',
  });

  const [selectedNode, setSelectedNode] = useState<IVRNode | null>(config.nodes[0]);

  // Load existing flow when in edit mode
  useEffect(() => {
    const loadExistingFlow = async () => {
      if (!isEditMode || !flowId || !currentOrganization?.id) return;

      setLoadingFlow(true);
      try {
        const response = await ivrFlowApi.getById(currentOrganization.id, parseInt(flowId));
        if (response.success && response.data) {
          const flow = response.data;
          // Map backend flow to frontend config
          setConfig({
            name: flow.flowName || '',
            description: flow.description || '',
            provider: 'twilio', // Default, could be stored in settings
            phoneNumber: flow.assignedNumbers?.[0] || '',
            language: flow.settings?.language || 'en-US',
            voice: flow.settings?.voice || 'alice',
            recordCalls: flow.settings?.recordCalls ?? true,
            transcribeCalls: flow.settings?.transcribeCalls ?? false,
            businessHours: flow.settings?.businessHours || 'default',
            afterHoursBehavior: flow.settings?.afterHoursBehavior || 'voicemail',
            nodes: flow.nodes || [{
              id: 'start',
              type: 'greeting',
              label: 'Welcome Greeting',
              message: 'Thank you for calling.',
              timeout: 5,
            }],
            currentNodeId: flow.startNodeId || 'start',
            business_id: flow.organizationId,
            organization_id: flow.organizationId,
            operator_id: flow.settings?.operator_id,
            service_id: flow.settings?.service_id,
          });
          if (flow.nodes?.length > 0) {
            setSelectedNode(flow.nodes[0]);
          }
        }
      } catch (error) {
        console.error('Error loading flow:', error);
        setSaveError('Failed to load IVR flow');
      } finally {
        setLoadingFlow(false);
      }
    };

    loadExistingFlow();
  }, [isEditMode, flowId, currentOrganization?.id]);

  // Load an existing IVR app for editing: ?operatorId=&serviceId= (from the IVR
  // Apps "Edit" action). Pulls the app's states from cpass and reverse-translates
  // them into the node graph.
  const editOperatorId = searchParams.get('operatorId');
  const editServiceId = searchParams.get('serviceId');
  useEffect(() => {
    const loadApp = async () => {
      if (!editOperatorId || !editServiceId) return;
      setLoadingFlow(true);
      setSaveError(null);
      try {
        const res = await stateDetailsService.list({ operatorId: editOperatorId, serviceId: editServiceId });
        if (res.success && res.data && res.data.length) {
          const { nodes } = buildNodesFromStates(res.data as any);
          const first: any = res.data[0];
          setConfig(prev => ({
            ...prev,
            name: editServiceId,
            nodes: nodes as any,
            currentNodeId: nodes[0]?.id || 'start',
            operator_id: editOperatorId,
            service_id: editServiceId,
            language: first.language || prev.language,
          }));
          if (nodes.length) setSelectedNode(nodes[0] as any);
        } else if (res.success) {
          // cpass's own state_details is empty for this app, and that is the
          // normal case rather than a fault: the table has one writer, /deploy,
          // which takes a flow authored here. Nothing pulls a switch's states
          // into it, so every app imported from a runtime lands with zero rows
          // and this screen used to show a blank template with an error over it.
          //
          // The flow itself does exist — the importer writes ivr_flows.nodes.
          // Translated through nodesFromImportedFlow rather than used as-is: the
          // importer's vocabulary only OVERLAPS this editor's (name vs label,
          // audioFiles vs audioConfig, choices vs options), so assigning it
          // straight through loaded a graph with every field blank. Looked up by
          // app rather than by organisation because the registry row and the flow
          // are filed under different owners; see IvrFlowService.findFlowForApp.
          const imported = await ivrFlowsService.forApp(editOperatorId, editServiceId);
          const flow = imported.success ? imported.data : null;
          if (flow && Array.isArray(flow.nodes) && flow.nodes.length) {
            const { nodes: importedNodes } = nodesFromImportedFlow(flow.nodes as any);
            setConfig(prev => ({
              ...prev,
              name: editServiceId,
              nodes: importedNodes as any,
              currentNodeId: flow.startNodeId || importedNodes[0]?.id || 'start',
              operator_id: editOperatorId,
              service_id: editServiceId,
              // The importer names a flow "<operator> / <service> / <language>";
              // trust that over the default rather than silently relabelling.
              language: (flow.flowName || '').split('/').pop()?.trim() || prev.language,
            }));
            const start = importedNodes.find((n: any) => n.id === flow.startNodeId) || importedNodes[0];
            // `as any`: this file redeclares IVRNode locally and its type union
            // omits 'submenu'. Same cast the state_details path above uses.
            if (start) setSelectedNode(start as any);
            setSaveError(null);
          } else {
            setSaveError(`No flow found for ${editOperatorId}/${editServiceId} — import it first (IVR Apps → Import from runtime), or build it here and save.`);
          }
        } else {
          setSaveError(res.error || 'Failed to load app states');
        }
      } catch (e) {
        setSaveError('Failed to load app for editing');
      } finally {
        setLoadingFlow(false);
      }
    };
    loadApp();
  }, [editOperatorId, editServiceId]);

  // Fetch phone numbers when business_id changes
  useEffect(() => {
    const fetchPhoneNumbers = async () => {
      if (!config.business_id) {
        setPhoneNumbers([]);
        return;
      }

      setLoadingPhones(true);
      try {
        const response = await phonesService.getPhoneNumbers({
          organizationId: config.business_id,
          perPage: 100, // Get all phone numbers for the business
        });

        if (response.success && response.data) {
          setPhoneNumbers(response.data.data || []);
        } else {
          setPhoneNumbers([]);
        }
      } catch (error) {
        console.error('Error fetching phone numbers:', error);
        setPhoneNumbers([]);
      } finally {
        setLoadingPhones(false);
      }
    };

    fetchPhoneNumbers();
  }, [config.business_id]);

  // Load the org's previously-uploaded/generated audio assets so they can be reused in any node.
  const loadAudioAssets = React.useCallback(async () => {
    if (!currentOrganization?.id) { setAudioAssets([]); return; }
    setLoadingAssets(true);
    try {
      const res = await audioApi.list({ organizationId: currentOrganization.id });
      const list = (res as any)?.data ?? (res as any) ?? [];
      setAudioAssets(Array.isArray(list) ? list : []);
    } catch {
      setAudioAssets([]);
    } finally {
      setLoadingAssets(false);
    }
  }, [currentOrganization?.id]);

  useEffect(() => { loadAudioAssets(); }, [loadAudioAssets]);

  // Handle phone number selection - auto-populate operator_id and service_id
  const handlePhoneNumberChange = (phoneNumberValue: string) => {
    const selectedPhone = phoneNumbers.find(p => p.cli_number === phoneNumberValue);

    if (selectedPhone) {
      setConfig(prev => ({
        ...prev,
        phoneNumber: phoneNumberValue,
        operator_id: selectedPhone.operator_id,
        service_id: selectedPhone.service_id,
        organization_id: prev.business_id,
      }));
    } else {
      setConfig(prev => ({
        ...prev,
        phoneNumber: phoneNumberValue,
        operator_id: undefined,
        service_id: undefined,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentOrganization?.id) {
      setSaveError('No organization selected');
      return;
    }

    if (!config.name) {
      setSaveError('IVR name is required');
      return;
    }

    setLoading(true);
    setSaveError(null);

    try {
      // Build the flow data for the backend
      const flowData = {
        flowName: config.name,
        description: config.description,
        status: 'DRAFT' as const,
        startNodeId: config.nodes[0]?.id || 'start',
        nodes: config.nodes,
        settings: {
          provider: config.provider,
          language: config.language,
          voice: config.voice,
          recordCalls: config.recordCalls,
          transcribeCalls: config.transcribeCalls,
          businessHours: config.businessHours,
          afterHoursBehavior: config.afterHoursBehavior,
          operator_id: config.operator_id,
          service_id: config.service_id,
        },
        assignedNumbers: config.phoneNumber ? [config.phoneNumber] : [],
        enabled: true,
      };

      let response;
      if (isEditMode && flowId) {
        // Update existing flow
        response = await ivrFlowApi.update(currentOrganization.id, parseInt(flowId), flowData);
      } else {
        // Create new flow
        response = await ivrFlowApi.create(currentOrganization.id, flowData);
      }

      if (response.success) {
        console.log('IVR flow saved:', response.data);
        navigate('/telephony/ivr');
      } else {
        setSaveError(response.error || 'Failed to save IVR flow');
      }
    } catch (error) {
      console.error('Error saving IVR:', error);
      setSaveError(error instanceof Error ? error.message : 'Failed to save IVR flow');
    } finally {
      setLoading(false);
    }
  };

  /**
   * The organisation this IVR belongs to.
   *
   * `organization_id` and `business_id` have always held the same value —
   * loading a saved flow sets both from `flow.organizationId`. But only
   * `business_id` is written when you choose an organisation; `organization_id`
   * was a COPY taken at one specific moment, inside handlePhoneNumberChange.
   *
   * So the copy was never made unless a phone number was picked, and a service
   * provider organisation has no phone numbers to pick — which is why pushing
   * as a super admin failed with "Organization ID is required" even though an
   * organisation was plainly selected on screen. Reading through to
   * `business_id` removes the window entirely.
   */
  const orgId = config.organization_id || config.business_id;

  /**
   * What the switch push genuinely needs, checked before sending.
   *
   * operator_id and service_id are @NotBlank on IVRFlowDeployDTO, and they are
   * only ever filled in by choosing a phone number. The old organization_id
   * guard happened to enforce that too — organization_id was copied in the same
   * handler — so removing the copy removed an accidental check and the request
   * started reaching the server without them.
   *
   * The server said "Bad Request" and nothing else, because Boot omits the
   * detail unless asked; the DTO's own "operator_id is required" never left the
   * building. Both ends are fixed: this refuses locally with the name of the
   * control to use, and RequestValidationAdvice now returns the field messages.
   */
  const pushBlocker = (): string | null => {
    if (!orgId) return 'Choose an organisation first.';
    if (!config.name) return 'Give the flow a name first.';
    if (!config.operator_id || !config.service_id) {
      return 'Choose a phone number first — it sets the operator and service this flow is pushed under.';
    }
    if (!config.nodes?.length) return 'Add at least one node before pushing.';
    return null;
  };

  const switchInput = () => ({
    name: config.name,
    operatorId: config.operator_id,
    serviceId: config.service_id,
    language: (config as any).language || 'en',
    nodes: config.nodes,
  });

  // Preview only. Nothing is written until the user reads this and confirms.
  const handlePreviewSwitch = async () => {
    // Guarded rather than cast: the switch write is scoped by organization, and
    // an undefined one would be sent as the string "undefined" and scope the
    // push to nothing recognisable. 
    // `|| !orgId` is not redundant: pushBlocker already covers it, but a
    // function returning null does not narrow orgId for the compiler the way
    // an inline check does, and the call below needs a string.
    const blocked = pushBlocker();
    if (blocked || !orgId) { setSwitchError(blocked ?? 'Choose an organisation first.'); return; }
    setSwitchError(null); setSwitchDone(null); setSwitchBusy(true);
    try {
      const res = await previewSwitchPush(switchInput() as any, switchOptions, orgId);
      if (res.success && res.data) setSwitchPlan(res.data);
      else setSwitchError(res.error || 'Could not read the switch');
    } finally { setSwitchBusy(false); }
  };

  const handleApplySwitch = async () => {
    const blocked = pushBlocker();
    if (blocked || !orgId) { setSwitchError(blocked ?? 'Choose an organisation first.'); return; }
    setSwitchError(null); setSwitchBusy(true);
    try {
      const res = await applySwitchPush(switchInput() as any, switchOptions, orgId, switchPlan?.previewHash);
      if (res.success && res.data) { setSwitchDone(res.data); setSwitchPlan(null); }
      else setSwitchError(res.error || 'Push failed');
    } finally { setSwitchBusy(false); }
  };

  const addNode = (type: IVRNode['type']) => {
    const newNode: IVRNode = {
      id: `node-${Date.now()}`,
      type,
      label: `New ${type.charAt(0).toUpperCase() + type.slice(1)}`,
      message: '',
      options: type === 'menu' ? [
        { digit: '1', label: 'Option 1', nextNode: '' },
      ] : undefined,
      timeout: 5,
    };

    setConfig(prev => ({
      ...prev,
      nodes: [...prev.nodes, newNode],
    }));
    setSelectedNode(newNode);
  };

  const updateNode = (nodeId: string, updates: Partial<IVRNode>) => {
    setConfig(prev => ({
      ...prev,
      nodes: prev.nodes.map(node =>
        node.id === nodeId ? { ...node, ...updates } : node
      ),
    }));
    if (selectedNode?.id === nodeId) {
      setSelectedNode(prev => prev ? { ...prev, ...updates } : null);
    }
  };

  // Insert a {{variable}} placeholder into the message textarea at the cursor
  // (falls back to appending), so dynamic data can be dropped into any prompt.
  const messageRef = useRef<HTMLTextAreaElement>(null);
  const insertVariable = (nodeId: string, placeholder: string) => {
    const el = messageRef.current;
    const current = config.nodes.find(n => n.id === nodeId)?.message || '';
    if (!el) { updateNode(nodeId, { message: current + placeholder }); return; }
    const start = el.selectionStart ?? current.length;
    const end = el.selectionEnd ?? current.length;
    const next = current.slice(0, start) + placeholder + current.slice(end);
    updateNode(nodeId, { message: next });
    // restore caret just after the inserted token
    requestAnimationFrame(() => {
      const pos = start + placeholder.length;
      try { el.focus(); el.setSelectionRange(pos, pos); } catch (_) { /* noop */ }
    });
  };

  const deleteNode = (nodeId: string) => {
    setConfig(prev => ({
      ...prev,
      nodes: prev.nodes.filter(node => node.id !== nodeId),
    }));
    setSelectedNode(null);
  };

  const addOption = (nodeId: string) => {
    const node = config.nodes.find(n => n.id === nodeId);
    if (!node) return;
    const options = node.options || [];

    const newOption = {
      digit: (options.length + 1).toString(),
      label: `Option ${options.length + 1}`,
      value: '',
      nextNode: '',
    };

    updateNode(nodeId, {
      options: [...options, newOption],
    });
  };

  // Show loading state while fetching existing flow
  if (loadingFlow) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-indigo-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading IVR flow...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/telephony/ivr')}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
          <div>
            <div className="flex items-center space-x-2">
              <PhoneCall className="w-6 h-6 text-green-600" />
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {isEditMode ? 'Edit IVR Application' : 'Create IVR Application'}
              </h1>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Build interactive voice response flows with Twilio & Exotel
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handlePreviewSwitch}
            disabled={switchBusy || !config.name || !config.operator_id || !config.service_id}
            className="flex items-center space-x-2 px-6 py-2.5 border border-blue-600 text-blue-700 rounded-lg hover:bg-blue-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
            title="Show what would change on the switch. Writes nothing."
          >
            {switchBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
            <span>{switchBusy ? 'Checking...' : 'Push to switch'}</span>
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !config.name}
            className="flex items-center space-x-2 px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            <span>{loading ? 'Saving...' : isEditMode ? 'Save Changes' : 'Create IVR'}</span>
          </button>
        </div>
      </div>

      {/* Save Error Message */}
      {saveError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-red-900">Save Failed</h4>
            <p className="text-sm text-red-700 mt-1">{saveError}</p>
          </div>
          <button
            onClick={() => setSaveError(null)}
            className="ml-auto text-red-600 hover:text-red-800"
          >
            ×
          </button>
        </div>
      )}

      {/* Deployment Status Messages */}
      {switchError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="font-medium text-red-900">Switch push stopped</h4>
            <p className="text-sm text-red-700 mt-1">{switchError}</p>
            <p className="text-xs text-red-600 mt-2">
              Nothing was written. The flow on the switch is unchanged.
            </p>
          </div>
        </div>
      )}

      {/* The plan, before anything is written. The counts are what makes this
          readable at a glance: "deletes 3" on a flow you thought you were only
          adding to is the moment to stop. */}
      {switchPlan && (
        <SwitchPushPreview
          plan={switchPlan}
          options={switchOptions}
          busy={switchBusy}
          onOptionsChange={setSwitchOptions}
          onRefresh={handlePreviewSwitch}
          onApply={handleApplySwitch}
          onCancel={() => setSwitchPlan(null)}
        />
      )}

      {switchDone && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start space-x-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="font-medium text-green-900">Live on {switchDone.provider}</h4>
            <p className="text-sm text-green-700 mt-1">
              {switchDone.rowsWritten} row(s) written{switchDone.pushId ? ` (push #${switchDone.pushId}, audited — Green Giraffe can roll it back)` : ''}.
              Calls arriving now will use this flow. Nothing was deleted.
            </p>
            {!!switchDone.failures?.length && (
              <ul className="text-xs text-red-700 mt-2 list-disc list-inside">
                {switchDone.failures.map((f, i) => <li key={i}>{f}</li>)}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('design')}
            className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition ${
              activeTab === 'design'
                ? 'border-green-600 text-green-600'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <PlayCircle className="w-4 h-4" />
            <span>Flow Design</span>
          </button>
          <button
            onClick={() => setActiveTab('flowchart')}
            className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition ${
              activeTab === 'flowchart'
                ? 'border-green-600 text-green-600'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <GitBranch className="w-4 h-4" />
            <span>Flow Diagram</span>
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition ${
              activeTab === 'settings'
                ? 'border-green-600 text-green-600'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <PhoneCall className="w-4 h-4" />
            <span>Settings</span>
          </button>
        </nav>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Sidebar - Node List */}
        {activeTab === 'design' && (
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <h3 className="font-medium text-gray-900 dark:text-white mb-4">IVR Flow Nodes</h3>

              <div className="space-y-2 mb-4">
                {config.nodes.map((node, index) => (
                  <div
                    key={node.id}
                    onClick={() => setSelectedNode(node)}
                    className={`p-3 rounded-lg border-2 cursor-pointer transition ${
                      selectedNode?.id === node.id
                        ? 'border-green-500 bg-green-50 dark:bg-green-900/30'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">#{index + 1}</span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{node.label}</span>
                      </div>
                      {node.id !== 'start' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteNode(node.id);
                          }}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{node.type}</p>
                  </div>
                ))}
              </div>

              <div className="border-t dark:border-gray-700 pt-4">
                <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Add Node:</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { type: 'menu', label: 'Menu', icon: Hash },
                    { type: 'input', label: 'Input', icon: Mic2 },
                    { type: 'transfer', label: 'Transfer', icon: PhoneCall },
                    { type: 'ai_agent', label: 'AI Agent', icon: PlayCircle },
                    { type: 'api_call', label: 'API Call', icon: Zap },
                    // Ring group: live on the switch ([acd_queue] dispatches
                    // queue_<ext> into the org's own internal context) and
                    // modelled in cpass — it was only ever missing here.
                    { type: 'queue', label: 'Ring Group', icon: Users },
                    // TwiML withdrawn 2026-08-31: there is no TwiML runtime in
                    // the AGI or the dialplan, and the body was never carried
                    // into the deployed state — a TwiML node silently became a
                    // plain announcement. Offering it invited people to build
                    // something that could not run. Saved flows still load and
                    // deploy, with a warning saying exactly that.
                    { type: 'voicemail', label: 'Voicemail', icon: Mail },
                    { type: 'hangup', label: 'Hangup', icon: PhoneOff },
                  ].map((nodeType) => {
                    const Icon = nodeType.icon;
                    return (
                      <button
                        key={nodeType.type}
                        onClick={() => addNode(nodeType.type as IVRNode['type'])}
                        className="flex items-center justify-center space-x-1 px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition text-gray-900 dark:text-white"
                      >
                        <Icon className="w-3 h-3" />
                        <span>{nodeType.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className={activeTab === 'design' ? 'lg:col-span-2' : 'lg:col-span-3'}>
          {activeTab === 'flowchart' ? (
            <GraphFlowDiagram
              nodes={config.nodes}
              currentNodeId={config.currentNodeId}
              onNodeClick={(node) => {
                setSelectedNode(node);
                setActiveTab('design');
              }}
              onAddNode={(type) => addNode(type)}
              onUpdateConnection={(sourceId, targetId) => {
                const sourceNode = config.nodes.find(n => n.id === sourceId);
                if (!sourceNode) return;
                if (sourceNode.type === 'menu') {
                  // Menu: fill the first option without a target, else append one.
                  const options = sourceNode.options || [];
                  const emptyOption = options.find(opt => !opt.nextNode);
                  if (emptyOption) {
                    updateNode(sourceId, {
                      options: options.map(opt =>
                        opt === emptyOption ? { ...opt, nextNode: targetId } : opt
                      ),
                    });
                  } else {
                    updateNode(sourceId, {
                      options: [...options, {
                        digit: (options.length + 1).toString(),
                        label: `Option ${options.length + 1}`,
                        nextNode: targetId,
                      }],
                    });
                  }
                } else if (sourceNode.type !== 'hangup') {
                  // Every other node has a single onward transition.
                  updateNode(sourceId, { nextNode: targetId });
                }
              }}
              onDisconnect={({ sourceId, targetId, kind, optionIndex }) => {
                const sourceNode = config.nodes.find(n => n.id === sourceId);
                if (!sourceNode) return;
                if (kind === 'next') {
                  if (sourceNode.nextNode === targetId) updateNode(sourceId, { nextNode: '' });
                } else if (sourceNode.options) {
                  updateNode(sourceId, {
                    options: sourceNode.options.map((opt, idx) =>
                      (optionIndex != null ? idx === optionIndex : opt.nextNode === targetId)
                        ? { ...opt, nextNode: '' } : opt
                    ),
                  });
                }
              }}
              onDeleteNode={(nodeId) => deleteNode(nodeId)}
            />
          ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            {activeTab === 'design' ? (
              selectedNode ? (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Node Label
                    </label>
                    <input
                      type="text"
                      value={selectedNode.label}
                      onChange={(e) => updateNode(selectedNode.id, { label: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Node Type
                    </label>
                    <select
                      value={selectedNode.type}
                      onChange={(e) => updateNode(selectedNode.id, { type: e.target.value as IVRNode['type'] })}
                      className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      disabled={selectedNode.id === 'start'}
                    >
                      <option value="greeting">Greeting</option>
                      <option value="menu">Menu (DTMF)</option>
                      <option value="input">Voice Input</option>
                      <option value="transfer">Transfer Call</option>
                      <option value="ai_agent">AI Agent</option>
                      <option value="api_call">API/Webhook Call</option>
                      <option value="twiml">TwiML Response</option>
                      <option value="voicemail">Voicemail</option>
                      <option value="hangup">Hangup</option>
                    </select>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Message / Prompt
                      </label>
                      <VariableMenu onInsert={(ph) => insertVariable(selectedNode.id, ph)} />
                    </div>
                    <textarea
                      ref={messageRef}
                      value={selectedNode.message}
                      onChange={(e) => updateNode(selectedNode.id, { message: e.target.value })}
                      rows={4}
                      className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Enter the message that will be played to the caller... Use {{caller_id}} for dynamic data."
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Insert <code className="text-blue-500">{'{{variables}}'}</code> to make this dynamic — resolved at call time (works for static playback and AI prompts).
                    </p>
                  </div>

                  {/* Next Node — single transition for non-menu nodes.
                      Menus chain via their options; hangup is terminal. */}
                  {selectedNode.type !== 'menu' && selectedNode.type !== 'hangup' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Next Node
                      </label>
                      <select
                        value={selectedNode.nextNode || ''}
                        onChange={(e) => updateNode(selectedNode.id, { nextNode: e.target.value })}
                        className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      >
                        <option value="">— End call / no next —</option>
                        {config.nodes
                          .filter((n) => n.id !== selectedNode.id)
                          .map((n) => (
                            <option key={n.id} value={n.id}>{n.label}</option>
                          ))}
                      </select>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Where the call goes after this node (for {selectedNode.type === 'api_call' ? 'a successful API call' : 'playback/input'}). Leave empty to end.
                      </p>
                    </div>
                  )}

                  {/* TTS and Audio Configuration */}
                  {(selectedNode.type === 'greeting' || selectedNode.type === 'menu' || selectedNode.type === 'input') && (
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center space-x-2">
                        <Volume2 className="w-4 h-4 text-blue-600" />
                        <span>Audio Configuration</span>
                      </h4>

                      <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                        {/* Text-to-Speech Section */}
                        <div>
                          <label className="flex items-center space-x-2 mb-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedNode.audioConfig?.useTextToSpeech || false}
                              onChange={(e) => updateNode(selectedNode.id, {
                                audioConfig: {
                                  ...selectedNode.audioConfig,
                                  useTextToSpeech: e.target.checked,
                                }
                              })}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                            />
                            <span className="text-sm font-medium text-gray-900 dark:text-white">Enable Text-to-Speech (TTS)</span>
                          </label>

                          {selectedNode.audioConfig?.useTextToSpeech && (
                            <div className="space-y-3 ml-6 pl-4 border-l-2 border-blue-300">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                  TTS Text
                                </label>
                                <textarea
                                  value={selectedNode.audioConfig?.text || ''}
                                  onChange={(e) => updateNode(selectedNode.id, {
                                    audioConfig: {
                                      ...selectedNode.audioConfig,
                                      useTextToSpeech: true,
                                      text: e.target.value,
                                    }
                                  })}
                                  rows={3}
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  placeholder="Enter text to be spoken by TTS engine..."
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                  This text will be converted to speech using the selected voice
                                </p>
                              </div>

                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    TTS Voice
                                  </label>
                                  <select
                                    value={selectedNode.audioConfig?.ttsVoice || 'alice'}
                                    onChange={(e) => updateNode(selectedNode.id, {
                                      audioConfig: {
                                        ...selectedNode.audioConfig,
                                        useTextToSpeech: true,
                                        ttsVoice: e.target.value,
                                      }
                                    })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                                  >
                                    <option value="alice">Alice (Female)</option>
                                    <option value="male">Male</option>
                                    <option value="Polly.Joanna">Polly - Joanna</option>
                                    <option value="Polly.Matthew">Polly - Matthew</option>
                                    <option value="Polly.Amy">Polly - Amy (British)</option>
                                  </select>
                                </div>

                                <div>
                                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    TTS Language
                                  </label>
                                  <select
                                    value={selectedNode.audioConfig?.ttsLanguage || 'en-US'}
                                    onChange={(e) => updateNode(selectedNode.id, {
                                      audioConfig: {
                                        ...selectedNode.audioConfig,
                                        useTextToSpeech: true,
                                        ttsLanguage: e.target.value,
                                      }
                                    })}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                                  >
                                    <option value="en-US">English (US)</option>
                                    <option value="en-IN">English (India)</option>
                                    <option value="hi-IN">Hindi</option>
                                    <option value="sw-KE">Swahili</option>
                                    <option value="es-ES">Spanish</option>
                                  </select>
                                </div>
                              </div>

                              <button
                                type="button"
                                onClick={async () => {
                                  const text = selectedNode.audioConfig?.text || '';
                                  if (!text) return;
                                  setTtsPreviewing(true);
                                  try {
                                    const res = await audioApi.previewTTS({
                                      text,
                                      language: selectedNode.audioConfig?.ttsLanguage,
                                      voice: selectedNode.audioConfig?.ttsVoice,
                                      organizationId: currentOrganization?.id,
                                    });
                                    if (res.success && res.data?.url) {
                                      const audio = new Audio(res.data.url);
                                      audio.onended = () => URL.revokeObjectURL(res.data!.url);
                                      await audio.play();
                                    } else {
                                      alert(res.error || 'TTS preview is not available.');
                                    }
                                  } finally {
                                    setTtsPreviewing(false);
                                  }
                                }}
                                disabled={!selectedNode.audioConfig?.text || ttsPreviewing}
                                className="flex items-center space-x-2 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                              >
                                <Play className="w-3 h-3" />
                                <span>{ttsPreviewing ? 'Generating…' : 'Preview TTS'}</span>
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Audio Files Section */}
                        <div className="pt-3 border-t border-blue-200 dark:border-blue-700">
                          <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                            Audio Files
                          </label>

                          <div className="space-y-2">
                            {selectedNode.audioConfig?.audioFiles && selectedNode.audioConfig.audioFiles.length > 0 ? (
                              <div className="space-y-2">
                                {selectedNode.audioConfig.audioFiles.map((file, index) => (
                                  <div key={index} className="flex items-center justify-between p-2 bg-white rounded-lg border border-gray-200">
                                    <div className="flex items-center space-x-2">
                                      <Volume2 className="w-4 h-4 text-gray-400" />
                                      <span className="text-sm text-gray-700">{file}</span>
                                    </div>
                                    <button
                                      onClick={() => {
                                        const newFiles = selectedNode.audioConfig?.audioFiles?.filter((_, i) => i !== index);
                                        updateNode(selectedNode.id, {
                                          audioConfig: {
                                            ...selectedNode.audioConfig,
                                            audioFiles: newFiles,
                                          }
                                        });
                                      }}
                                      className="text-red-600 hover:text-red-800"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-center py-4 bg-white rounded-lg border-2 border-dashed border-gray-300">
                                <p className="text-sm text-gray-500">No audio files uploaded</p>
                              </div>
                            )}

                            {/* Reuse a previously uploaded/generated asset from the org library */}
                            {audioAssets.length > 0 && (
                              <select
                                value=""
                                onChange={(e) => {
                                  const picked = audioAssets.find((a) => String(a.id) === e.target.value);
                                  if (!picked?.url) return;
                                  const currentFiles = selectedNode.audioConfig?.audioFiles || [];
                                  if (currentFiles.includes(picked.url)) return;
                                  updateNode(selectedNode.id, {
                                    audioConfig: {
                                      ...selectedNode.audioConfig,
                                      audioFiles: [...currentFiles, picked.url],
                                    }
                                  });
                                }}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-blue-500 mb-2"
                              >
                                <option value="">{loadingAssets ? 'Loading library…' : `Add from library (${audioAssets.length})…`}</option>
                                {audioAssets.map((a) => (
                                  <option key={a.id} value={a.id}>
                                    {a.name || `asset #${a.id}`}{a.language ? ` (${a.language})` : ''}
                                  </option>
                                ))}
                              </select>
                            )}

                            <label className={`flex items-center justify-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg transition text-sm ${uploadingAudio ? 'opacity-50 cursor-wait' : 'hover:bg-white cursor-pointer'}`}>
                              <Upload className="w-4 h-4" />
                              <span>{uploadingAudio ? 'Uploading…' : 'Upload Audio File'}</span>
                              <input
                                type="file"
                                disabled={uploadingAudio}
                                accept="audio/*,.mp3,.wav"
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  if (file.size > 5 * 1024 * 1024) { alert('File exceeds 5MB.'); e.target.value = ''; return; }
                                  setUploadingAudio(true);
                                  try {
                                    const res = await audioApi.upload(file, {
                                      organizationId: currentOrganization?.id,
                                      business_id: config.business_id,
                                      language: selectedNode.audioConfig?.ttsLanguage,
                                      name: file.name,
                                    });
                                    // Store the served URL the runtime can fetch; fall back to the name on failure.
                                    const stored = (res.success && res.data?.url) ? res.data.url : file.name;
                                    if (!res.success) alert(res.error || 'Upload failed; keeping the filename only.');
                                    else loadAudioAssets(); // refresh the library so the new asset is reusable
                                    const currentFiles = selectedNode.audioConfig?.audioFiles || [];
                                    updateNode(selectedNode.id, {
                                      audioConfig: {
                                        ...selectedNode.audioConfig,
                                        audioFiles: [...currentFiles, stored],
                                      }
                                    });
                                  } finally {
                                    setUploadingAudio(false);
                                    e.target.value = '';
                                  }
                                }}
                                className="hidden"
                              />
                            </label>
                            <p className="text-xs text-gray-500">
                              Supported formats: MP3, WAV (max 5MB)
                            </p>
                          </div>
                        </div>

                        {/* Use Audio First Option */}
                        {selectedNode.audioConfig?.text &&
                         selectedNode.audioConfig?.audioFiles &&
                         selectedNode.audioConfig.audioFiles.length > 0 && (
                          <div className="pt-3 border-t border-blue-200">
                            <label className="flex items-start space-x-3 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedNode.audioConfig?.useAudioFirst || false}
                                onChange={(e) => updateNode(selectedNode.id, {
                                  audioConfig: {
                                    ...selectedNode.audioConfig,
                                    useAudioFirst: e.target.checked,
                                  }
                                })}
                                className="mt-0.5 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                              />
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  Prefer audio files over TTS
                                </div>
                                <div className="text-xs text-gray-500">
                                  Play audio file first, use TTS only if file is unavailable
                                </div>
                              </div>
                            </label>
                          </div>
                        )}

                        {/* Audio Playback Mode Indicator */}
                        <div className="pt-2 border-t border-blue-200">
                          <div className="flex items-center space-x-2 text-xs">
                            <span className="font-semibold text-gray-700">Playback Mode:</span>
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full font-medium">
                              {!selectedNode.audioConfig?.useTextToSpeech &&
                               (!selectedNode.audioConfig?.audioFiles || selectedNode.audioConfig.audioFiles.length === 0)
                                ? 'No audio configured'
                                : selectedNode.audioConfig?.useTextToSpeech &&
                                  (!selectedNode.audioConfig?.audioFiles || selectedNode.audioConfig.audioFiles.length === 0)
                                ? 'TTS only'
                                : !selectedNode.audioConfig?.useTextToSpeech &&
                                  selectedNode.audioConfig?.audioFiles &&
                                  selectedNode.audioConfig.audioFiles.length > 0
                                ? 'Audio files only'
                                : selectedNode.audioConfig?.useAudioFirst
                                ? 'Audio files (TTS fallback)'
                                : 'TTS (Audio fallback)'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {(selectedNode.type === 'menu' || (selectedNode.options && selectedNode.options.length > 0)) && (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Menu Options <span className="text-xs text-gray-400">(DTMF branches)</span>
                        </label>
                        <button
                          onClick={() => addOption(selectedNode.id)}
                          className="flex items-center space-x-1 text-sm text-green-600 hover:text-green-700"
                        >
                          <Plus className="w-4 h-4" />
                          <span>Add Option</span>
                        </button>
                      </div>
                      <div className="space-y-3">
                        {selectedNode.options?.map((option, index) => {
                          const setOpt = (patch: Partial<typeof option>) => {
                            const newOptions = (selectedNode.options || []).map((o, i) => i === index ? { ...o, ...patch } : o);
                            updateNode(selectedNode.id, { options: newOptions });
                          };
                          return (
                          <div key={index} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg space-y-2">
                            <div className="flex items-center space-x-3">
                              <input
                                type="text" value={option.digit} maxLength={1}
                                onChange={(e) => setOpt({ digit: e.target.value })}
                                className="w-12 px-2 py-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-600 dark:text-white rounded text-center"
                                title="DTMF key"
                              />
                              <input
                                type="text" value={option.label} placeholder="Option label (msg)"
                                onChange={(e) => setOpt({ label: e.target.value })}
                                className="flex-1 px-3 py-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-600 dark:text-white dark:placeholder-gray-400 rounded"
                              />
                              <MoveRight className="w-4 h-4 text-gray-400" />
                              <select
                                value={option.nextNode}
                                onChange={(e) => setOpt({ nextNode: e.target.value })}
                                className="w-40 px-2 py-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-600 dark:text-white rounded text-sm"
                              >
                                <option value="">Next Node</option>
                                {config.nodes.filter(n => n.id !== selectedNode.id).map(n => (
                                  <option key={n.id} value={n.id}>{n.label}</option>
                                ))}
                              </select>
                              <button
                                onClick={() => updateNode(selectedNode.id, { options: (selectedNode.options || []).filter((_, i) => i !== index) })}
                                className="p-1 text-red-500 hover:bg-red-50 rounded" title="Remove option"
                              ><Trash2 className="w-4 h-4" /></button>
                            </div>
                            <input
                              type="text" value={option.value || ''}
                              onChange={(e) => setOpt({ value: e.target.value })}
                              className="w-full px-3 py-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-600 dark:text-white dark:placeholder-gray-400 rounded text-sm font-mono"
                              placeholder="value — destination / product id / wss:// agent URL (optional)"
                            />
                          </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {selectedNode.type === 'transfer' && (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Transfer to</label>
                        <select
                          value={selectedNode.transferType || 'number'}
                          onChange={(e) => updateNode(selectedNode.id, { transferType: e.target.value as any })}
                          className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-green-500"
                        >
                          <option value="number">Phone number (PSTN)</option>
                          <option value="sip">SIP endpoint (dial / refer)</option>
                          <option value="agent">AI / voice agent</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      {(selectedNode.transferType || 'number') === 'number' && (
                        <input type="tel" value={selectedNode.transferNumber || ''}
                          onChange={(e) => updateNode(selectedNode.id, { transferNumber: e.target.value })}
                          className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-green-500"
                          placeholder="+1234567890" />
                      )}
                      {selectedNode.transferType === 'sip' && (() => {
                        // Pick a configured sip_transfer_profile (fills endpoint +
                        // method from its templates/mode) or enter a custom endpoint.
                        const selId = selectedNode.transferProfileId
                          ?? transferProfiles.find(p => p.name && p.name === selectedNode.transferProfileName)?.id
                          ?? '';
                        const applyProfile = (id: string) => {
                          if (!id) { updateNode(selectedNode.id, { transferProfileId: undefined, transferProfileName: undefined }); return; }
                          const p = transferProfiles.find(pp => String(pp.id) === id);
                          if (!p) return;
                          const method = p.mode === 'SIPDial' ? 'dial' : 'refer';
                          const endpoint = (method === 'dial' ? p.dial_dest_template : p.refer_to_template) || p.scope_value || '';
                          updateNode(selectedNode.id, {
                            transferProfileId: p.id, transferProfileName: p.name || p.scope_value,
                            sipEndpoint: endpoint, transferMethod: method,
                          });
                        };
                        return (
                          <div className="space-y-2">
                            <select value={String(selId)} onChange={(e) => applyProfile(e.target.value)}
                              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm">
                              <option value="">Custom endpoint</option>
                              {transferProfiles.map(p => (
                                <option key={p.id} value={p.id}>
                                  {(p.name || p.scope_value)} — {p.mode || 'SIPRefer'} ({p.scope_value})
                                </option>
                              ))}
                            </select>
                            {selId ? (
                              <div>
                                <p className="text-xs text-gray-500 font-mono">→ {selectedNode.sipEndpoint} <span className="text-gray-400">({selectedNode.transferMethod || 'refer'})</span></p>
                                <p className="text-xs text-gray-400">Copied from the profile — the flow stores the endpoint flat, not a link, so later profile edits don't change this deployed flow.</p>
                              </div>
                            ) : (
                              <div className="grid grid-cols-3 gap-3">
                                <input type="text" value={selectedNode.sipEndpoint || ''}
                                  onChange={(e) => updateNode(selectedNode.id, { sipEndpoint: e.target.value })}
                                  className="col-span-2 px-4 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 rounded-lg font-mono text-sm"
                                  placeholder="sip:user@host or PJSIP/endpoint" />
                                <select value={selectedNode.transferMethod || 'refer'}
                                  onChange={(e) => updateNode(selectedNode.id, { transferMethod: e.target.value as any })}
                                  className="px-2 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm">
                                  <option value="refer">REFER</option><option value="dial">Dial</option>
                                </select>
                              </div>
                            )}
                            {transferProfiles.length === 0 && (
                              <p className="text-xs text-gray-400">No transfer profiles configured — enter a custom endpoint, or add profiles under Transfer Profiles.</p>
                            )}
                          </div>
                        );
                      })()}
                      {selectedNode.transferType === 'agent' && (
                        <input type="text" value={selectedNode.agentUrl || ''} list="managed-agents"
                          onChange={(e) => updateNode(selectedNode.id, { agentUrl: e.target.value })}
                          className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 rounded-lg font-mono text-sm"
                          placeholder="wss:// voice-agent endpoint — or pick a managed agent" />
                      )}
                      {selectedNode.transferType === 'other' && (
                        <input type="text" value={selectedNode.transferNumber || ''}
                          onChange={(e) => updateNode(selectedNode.id, { transferNumber: e.target.value })}
                          className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 rounded-lg"
                          placeholder="destination" />
                      )}
                    </div>
                  )}

                  {selectedNode.type === 'ai_agent' && (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Voice agent</label>
                        <select
                          value={selectedNode.agentMode || (selectedNode.agentUrl ? 'external' : 'internal')}
                          onChange={(e) => updateNode(selectedNode.id, { agentMode: e.target.value as any })}
                          className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-green-500"
                        >
                          <option value="internal">Internal — managed by our platform</option>
                          <option value="external">External — third-party agent URL</option>
                        </select>
                      </div>
                      {(selectedNode.agentMode || (selectedNode.agentUrl ? 'external' : 'internal')) === 'internal' ? (
                        <div>
                          <input type="text" value={selectedNode.aiAgent || ''}
                            onChange={(e) => updateNode(selectedNode.id, { aiAgent: e.target.value })}
                            className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 rounded-lg"
                            placeholder="Managed agent name / id (optional)" />
                          <p className="text-xs text-gray-500 mt-1">The websocket URL is resolved at call time for managed agents.</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <input type="text" value={selectedNode.agentUrl || ''} list="managed-agents"
                            onChange={(e) => updateNode(selectedNode.id, { agentUrl: e.target.value })}
                            className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 rounded-lg font-mono text-sm"
                            placeholder="wss:// agent endpoint (inbound) — pick a managed agent or paste" />
                          <input type="text" value={selectedNode.agentUrlOut || ''} list="managed-agents"
                            onChange={(e) => updateNode(selectedNode.id, { agentUrlOut: e.target.value })}
                            className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 rounded-lg font-mono text-sm"
                            placeholder="wss:// agent endpoint (outbound) — optional" />
                          <p className="text-xs text-gray-500">Pick a managed AI Agent (its wsurl) or paste a third-party endpoint.</p>
                          <datalist id="managed-agents">
                            {aiAgents.filter(a => a.wsurl).map(a => (
                              <option key={a.id} value={a.wsurl}>{a.name}</option>
                            ))}
                          </datalist>
                        </div>
                      )}
                    </div>
                  )}

                  {selectedNode.type === 'api_call' && (
                    <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <h4 className="font-medium text-gray-900 flex items-center space-x-2">
                        <Zap className="w-4 h-4 text-blue-600" />
                        <span>API/Webhook Configuration</span>
                      </h4>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          API Endpoint URL <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="url"
                          value={selectedNode.apiCall?.url || ''}
                          onChange={(e) => updateNode(selectedNode.id, {
                            apiCall: { ...selectedNode.apiCall, url: e.target.value, method: selectedNode.apiCall?.method || 'POST' }
                          })}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="https://api.example.com/webhook"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          This endpoint will be called during the IVR flow. Caller data will be sent as payload.
                        </p>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            HTTP Method
                          </label>
                          <select
                            value={selectedNode.apiCall?.method || 'POST'}
                            onChange={(e) => updateNode(selectedNode.id, {
                              apiCall: { ...selectedNode.apiCall, url: selectedNode.apiCall?.url || '', method: e.target.value as 'GET' | 'POST' | 'PUT' }
                            })}
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="GET">GET</option>
                            <option value="POST">POST</option>
                            <option value="PUT">PUT</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            On Success → Next Node
                          </label>
                          <select
                            value={selectedNode.apiCall?.onSuccess || ''}
                            onChange={(e) => updateNode(selectedNode.id, {
                              apiCall: { ...selectedNode.apiCall, url: selectedNode.apiCall?.url || '', method: selectedNode.apiCall?.method || 'POST', onSuccess: e.target.value }
                            })}
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="">Select Node</option>
                            {config.nodes.filter(n => n.id !== selectedNode.id).map(n => (
                              <option key={n.id} value={n.id}>{n.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Request Body (JSON)
                        </label>
                        <textarea
                          value={selectedNode.apiCall?.body || ''}
                          onChange={(e) => updateNode(selectedNode.id, {
                            apiCall: { ...selectedNode.apiCall, url: selectedNode.apiCall?.url || '', method: selectedNode.apiCall?.method || 'POST', body: e.target.value }
                          })}
                          rows={4}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                          placeholder='{\n  "caller": "{{caller_number}}",\n  "input": "{{user_input}}"\n}'
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Use variables: {`{{caller_number}}`}, {`{{user_input}}`}, {`{{call_id}}`}
                        </p>
                      </div>

                      <div className="bg-white p-3 rounded border border-gray-200">
                        <p className="text-xs font-medium text-gray-700 mb-2">💡 Use Cases:</p>
                        <ul className="text-xs text-gray-600 space-y-1 list-disc list-inside">
                          <li>Fetch customer data from CRM (Salesforce, HubSpot)</li>
                          <li>Verify account balance or order status</li>
                          <li>Trigger notifications or create tickets</li>
                          <li>Dynamic routing based on API response</li>
                        </ul>
                      </div>

                      <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-200">
                        <p className="text-xs font-bold text-blue-900 mb-3 flex items-center space-x-2">
                          <Code className="w-4 h-4" />
                          <span>📋 Required API Response Format</span>
                        </p>

                        <div className="space-y-3">
                          <div>
                            <p className="text-xs font-semibold text-gray-800 mb-1">Your API must return JSON in this format:</p>
                            <pre className="bg-gray-900 text-green-400 p-3 rounded text-xs overflow-x-auto font-mono">
{`{
  "status": "success",
  "message": "Welcome back, John!",
  "data": {
    "customer_name": "John Doe",
    "account_balance": 1250.50,
    "account_status": "active"
  },
  "next_action": {
    "type": "continue",
    "node_id": "menu-main"
  }
}`}
                            </pre>
                          </div>

                          <div className="bg-white p-3 rounded border border-blue-200">
                            <p className="text-xs font-semibold text-gray-800 mb-2">Response Fields:</p>
                            <div className="space-y-2 text-xs text-gray-700">
                              <div>
                                <code className="bg-blue-100 px-1.5 py-0.5 rounded font-mono text-blue-900">status</code>
                                <span className="ml-2 text-red-600">*required</span>
                                <p className="ml-2 mt-0.5 text-gray-600">Either <code>"success"</code> or <code>"error"</code></p>
                              </div>
                              <div>
                                <code className="bg-blue-100 px-1.5 py-0.5 rounded font-mono text-blue-900">message</code>
                                <span className="ml-2 text-gray-500">optional</span>
                                <p className="ml-2 mt-0.5 text-gray-600">Text to be spoken to the caller</p>
                              </div>
                              <div>
                                <code className="bg-blue-100 px-1.5 py-0.5 rounded font-mono text-blue-900">data</code>
                                <span className="ml-2 text-gray-500">optional</span>
                                <p className="ml-2 mt-0.5 text-gray-600">Object with custom data (accessible in subsequent nodes)</p>
                              </div>
                              <div>
                                <code className="bg-blue-100 px-1.5 py-0.5 rounded font-mono text-blue-900">next_action</code>
                                <span className="ml-2 text-gray-500">optional</span>
                                <p className="ml-2 mt-0.5 text-gray-600">Control flow: <code>continue</code>, <code>transfer</code>, <code>hangup</code></p>
                              </div>
                            </div>
                          </div>

                          <div className="bg-yellow-50 p-2 rounded border border-yellow-300">
                            <p className="text-xs font-semibold text-yellow-900 mb-1">⚠️ Important Notes:</p>
                            <ul className="text-xs text-yellow-800 space-y-0.5 list-disc list-inside">
                              <li>Response must be valid JSON with <code>Content-Type: application/json</code></li>
                              <li>HTTP 200 status = success path, non-200 = error path</li>
                              <li>Timeout: 10 seconds (configure in Advanced settings)</li>
                              <li>Variables in <code>data</code> can be used as <code>{`{{data.field_name}}`}</code></li>
                            </ul>
                          </div>

                          <div>
                            <p className="text-xs font-semibold text-gray-800 mb-1">Example Error Response:</p>
                            <pre className="bg-gray-900 text-red-400 p-2 rounded text-xs overflow-x-auto font-mono">
{`{
  "status": "error",
  "message": "Account not found",
  "error_code": "ACCOUNT_NOT_FOUND"
}`}
                            </pre>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedNode.type === 'twiml' && (
                    <div className="space-y-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                      <h4 className="font-medium text-gray-900 flex items-center space-x-2">
                        <Code className="w-4 h-4 text-purple-600" />
                        <span>TwiML XML Configuration</span>
                      </h4>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          TwiML XML Response <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          value={selectedNode.twiml?.xml || ''}
                          onChange={(e) => updateNode(selectedNode.id, {
                            twiml: { xml: e.target.value, validateXML: selectedNode.twiml?.validateXML ?? true }
                          })}
                          rows={12}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-sm"
                          placeholder={`<?xml version="1.0" encoding="UTF-8"?>\n<Response>\n  <Say voice="alice">Hello! This is a TwiML response.</Say>\n  <Gather action="/process" method="POST" numDigits="1">\n    <Say>Press 1 for Sales, 2 for Support</Say>\n  </Gather>\n  <Redirect>/menu</Redirect>\n</Response>`}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Enter valid Twilio TwiML XML. This provides full control over call flow.
                        </p>
                      </div>

                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedNode.twiml?.validateXML ?? true}
                          onChange={(e) => updateNode(selectedNode.id, {
                            twiml: { xml: selectedNode.twiml?.xml || '', validateXML: e.target.checked }
                          })}
                          className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-2 focus:ring-purple-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Validate XML before deployment</span>
                      </label>

                      <div className="bg-white p-3 rounded border border-gray-200">
                        <p className="text-xs font-medium text-gray-700 mb-2">📚 TwiML Verbs:</p>
                        <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                          <div><code className="bg-gray-100 px-1 rounded">&lt;Say&gt;</code> - Text to speech</div>
                          <div><code className="bg-gray-100 px-1 rounded">&lt;Play&gt;</code> - Play audio file</div>
                          <div><code className="bg-gray-100 px-1 rounded">&lt;Gather&gt;</code> - Collect input</div>
                          <div><code className="bg-gray-100 px-1 rounded">&lt;Record&gt;</code> - Record audio</div>
                          <div><code className="bg-gray-100 px-1 rounded">&lt;Dial&gt;</code> - Make a call</div>
                          <div><code className="bg-gray-100 px-1 rounded">&lt;Redirect&gt;</code> - Redirect flow</div>
                          <div><code className="bg-gray-100 px-1 rounded">&lt;Pause&gt;</code> - Add silence</div>
                          <div><code className="bg-gray-100 px-1 rounded">&lt;Hangup&gt;</code> - End call</div>
                        </div>
                        <a
                          href="https://www.twilio.com/docs/voice/twiml"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-purple-600 hover:text-purple-700 mt-2 inline-block"
                        >
                          View full TwiML documentation →
                        </a>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Timeout (seconds)
                    </label>
                    <input
                      type="number"
                      value={selectedNode.timeout}
                      onChange={(e) => updateNode(selectedNode.id, { timeout: parseInt(e.target.value) })}
                      min="3"
                      max="30"
                      className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  Select a node from the left panel to edit its properties
                </div>
              )
            ) : (
              <div className="space-y-6">
                {/* Organization Filter */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
                  <OrganizationFilter
                    value={config.business_id || ''}
                    onChange={(businessId) => setConfig({
                      ...config,
                      business_id: businessId,
                      // Written together so a saved flow carries the same value
                      // it is pushed with, rather than relying on a later step
                      // to copy it across.
                      organization_id: businessId,
                    })}
                    className="w-full md:w-96"
                    requireSelection={true}
                    allowServiceProviderSelection={true}
                  />
                </div>

                {/* Deployment Configuration */}
                <div className={`rounded-lg border-2 p-4 ${config.operator_id && config.service_id ? 'bg-green-50 dark:from-green-900/30 dark:to-emerald-900/30 border-green-200 dark:border-green-700' : 'bg-blue-50 dark:from-blue-900/30 dark:to-indigo-900/30 border-blue-200 dark:border-blue-700'}`}>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
                    {config.operator_id && config.service_id ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <Rocket className="w-4 h-4 text-blue-600" />
                    )}
                    <span>Deployment Configuration</span>
                    {config.operator_id && config.service_id ? (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Ready to Deploy</span>
                    ) : (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Auto-filled from Phone Number</span>
                    )}
                  </h4>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-4">
                    These fields are auto-populated when you select a phone number. They are used to deploy the IVR flow to the state machine.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Organization ID
                      </label>
                      <input
                        type="text"
                        value={config.organization_id || ''}
                        readOnly
                        className={`w-full px-4 py-2.5 border rounded-lg cursor-not-allowed ${config.organization_id ? 'border-green-300 bg-green-50 text-green-800 dark:border-green-600 dark:bg-green-900/30 dark:text-green-400' : 'border-gray-200 bg-gray-50 text-gray-600 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-400'}`}
                        placeholder="Select a phone number"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Auto-filled from business</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Operator ID
                      </label>
                      <input
                        type="text"
                        value={config.operator_id || ''}
                        readOnly
                        className={`w-full px-4 py-2.5 border rounded-lg cursor-not-allowed ${config.operator_id ? 'border-green-300 bg-green-50 text-green-800 dark:border-green-600 dark:bg-green-900/30 dark:text-green-400' : 'border-gray-200 bg-gray-50 text-gray-600 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-400'}`}
                        placeholder="Select a phone number"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Auto-filled from phone number</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Service ID
                      </label>
                      <input
                        type="text"
                        value={config.service_id || ''}
                        readOnly
                        className={`w-full px-4 py-2.5 border rounded-lg cursor-not-allowed ${config.service_id ? 'border-green-300 bg-green-50 text-green-800 dark:border-green-600 dark:bg-green-900/30 dark:text-green-400' : 'border-gray-200 bg-gray-50 text-gray-600 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-400'}`}
                        placeholder="Select a phone number"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Auto-filled from phone number</p>
                    </div>
                  </div>
                  {(!config.operator_id || !config.service_id) && (
                    <div className="mt-3 flex items-center space-x-2 text-amber-700 bg-amber-50 px-3 py-2 rounded-lg">
                      <AlertCircle className="w-4 h-4" />
                      <span className="text-xs">Select a phone number below to auto-fill deployment configuration</span>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      IVR Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={config.name}
                      onChange={(e) => setConfig({ ...config, name: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="e.g., Main Support IVR"
                      required
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Description
                    </label>
                    <textarea
                      value={config.description}
                      onChange={(e) => setConfig({ ...config, description: e.target.value })}
                      rows={3}
                      className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Provider
                    </label>
                    <select
                      value={config.provider}
                      onChange={(e) => setConfig({ ...config, provider: e.target.value as any })}
                      className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="twilio">Twilio</option>
                      <option value="exotel">Exotel</option>
                      <option value="both">Both (Multi-provider)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={config.phoneNumber}
                      onChange={(e) => handlePhoneNumberChange(e.target.value)}
                      className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      disabled={!config.business_id || loadingPhones}
                    >
                      <option value="">
                        {loadingPhones
                          ? 'Loading phone numbers...'
                          : !config.business_id
                          ? 'Select an organization first'
                          : phoneNumbers.length === 0
                          ? 'No phone numbers available'
                          : 'Select Phone Number'}
                      </option>
                      {phoneNumbers.map((phone) => (
                        <option key={phone.id} value={phone.cli_number}>
                          {phone.cli_number} - {phone.transcribe_name}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Selecting a phone number will auto-fill Operator ID and Service ID
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Voice
                    </label>
                    <select
                      value={config.voice}
                      onChange={(e) => setConfig({ ...config, voice: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="alice">Alice (Female)</option>
                      <option value="male">Male</option>
                      <option value="Polly.Joanna">Amazon Polly - Joanna</option>
                      <option value="Polly.Matthew">Amazon Polly - Matthew</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Language
                    </label>
                    <select
                      value={config.language}
                      onChange={(e) => setConfig({ ...config, language: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="en-US">English (US)</option>
                      <option value="en-IN">English (India)</option>
                      <option value="hi-IN">Hindi (India)</option>
                      <option value="es-ES">Spanish</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Business Hours
                    </label>
                    <select
                      value={config.businessHours}
                      onChange={(e) => setConfig({ ...config, businessHours: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="default">Default (9AM - 6PM)</option>
                      <option value="24x7">24/7</option>
                      <option value="custom">Custom Hours</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      After Hours Behavior
                    </label>
                    <select
                      value={config.afterHoursBehavior}
                      onChange={(e) => setConfig({ ...config, afterHoursBehavior: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="voicemail">Send to Voicemail</option>
                      <option value="message">Play Message & Hangup</option>
                      <option value="transfer">Transfer to Mobile</option>
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <div className="space-y-3">
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={config.recordCalls}
                          onChange={(e) => setConfig({ ...config, recordCalls: e.target.checked })}
                          className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-2 focus:ring-green-500"
                        />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Record all calls</span>
                      </label>

                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={config.transcribeCalls}
                          onChange={(e) => setConfig({ ...config, transcribeCalls: e.target.checked })}
                          className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-2 focus:ring-green-500"
                        />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Enable call transcription</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IVRCreation;
